import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { PLAN_PROMPT_MAX, type PlanStartInput, planStartCall } from '../lib/plan-start';
import type { RepoRow } from '../lib/repos';
import { CopyAsCall } from './CopyAsCall';
import { Field } from './Field';
import { Select } from './Select';
import { Button, buttonClass } from './ui/Button';

// The scout-start form (arc 101 slice 07). Presenter — <StartPlanModal/> owns
// the POST, the repo list and the verdict; this owns the two things a human
// types, which are exactly the two fields `PlanStartInputSchema` takes.
//
// THE REPO IS PICKED, NEVER TYPED. `POST /v1/plans` admits only a repo this
// account has ENABLED, and it folds "not yours" into the same 404 as "not
// enabled" on purpose (a foreign repo is never confirmed to exist) — so a typo
// and a genuinely absent repo read identically. The console already knows the
// list, so it offers it: the one shape that cannot be mistyped, the rule the
// grants subject picker and the box owner picker hold.
//
// THE SNIPPET IS BUILT FROM THE DRAFT, and withheld until the draft is one the
// server would accept. `<CopyAsCall/>` promises "the call this button makes";
// printing it over a blank repo or an empty ask would hand the reader a command
// that can only 400 — the rule the box briefing form already holds when its box
// is blank.

export type StartPlanFormProps = {
  /** The account's enabled repos — a plan can only name one the API admits. */
  readonly repos: readonly RepoRow[];
  /** True while the start is in flight. */
  readonly pending: boolean;
  readonly onSubmit: (input: PlanStartInput) => void;
  readonly onCancel: () => void;
};

export function StartPlanForm(props: StartPlanFormProps): JSX.Element {
  const [repo, setRepo] = createSignal('');
  const [prompt, setPrompt] = createSignal('');

  /** The exact value the POST would carry — trimmed here, as the schema trims. */
  const draft = (): PlanStartInput => ({ repo: repo(), prompt: prompt().trim() });
  // The repo picker carries no native `required`: the themed Select is a button, so
  // the browser cannot gate on it. This check is what stops an unpicked repo.
  const valid = (): boolean => draft().repo !== '' && draft().prompt !== '';

  const submit = (e: Event): void => {
    e.preventDefault();
    if (!valid() || props.pending) return;
    props.onSubmit(draft());
  };

  return (
    <form onSubmit={submit}>
      <div class="field-group">
        <Field
          label={t('fleet.plans.start.repo')}
          id="start-plan-repo"
          desc={props.repos.length === 0 ? t('fleet.plans.start.repo_empty') : undefined}
        >
          {(a11y) => (
            <Select
              a11y={a11y}
              label={t('fleet.plans.start.repo')}
              block
              value={repo()}
              options={[
                { value: '', label: t('fleet.plans.start.repo_placeholder') },
                ...props.repos.map((r) => ({ value: r.full_name, label: r.full_name })),
              ]}
              onChange={setRepo}
            />
          )}
        </Field>

        <Field
          label={t('fleet.plans.start.prompt')}
          id="start-plan-prompt"
          desc={t('fleet.plans.start.prompt_desc')}
        >
          {(a11y) => (
            <textarea
              {...a11y}
              class="textarea"
              rows="6"
              maxlength={PLAN_PROMPT_MAX}
              value={prompt()}
              onInput={(e) => setPrompt(e.currentTarget.value)}
              placeholder={t('fleet.plans.start.prompt_placeholder')}
              required
            />
          )}
        </Field>
      </div>

      <Show when={valid()}>
        <CopyAsCall
          calls={[{ label: t('fleet.plans.start.submit'), call: planStartCall(draft()) }]}
        />
      </Show>

      <div class="dialog__footer">
        <Button variant="outline" onClick={props.onCancel}>
          {t('fleet.plans.start.cancel')}
        </Button>
        <button type="submit" class={buttonClass()} disabled={!valid() || props.pending}>
          {t(props.pending ? 'fleet.plans.start.submitting' : 'fleet.plans.start.submit')}
        </button>
      </div>
    </form>
  );
}
